import { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { TRAVELPAYOUTS, TP_SCRIPT_SRC } from "../config/travelpayouts";
import { trackEvent } from "../lib/tracking";

const POPULAR_DESTINATIONS = ["IST", "DXB", "MOW", "LAS", "NYC", "LON"];

function setDomainCookie(name, value) {
  const d = new Date();
  d.setTime(d.getTime() + 31536000000);
  const host = window.location.hostname.split(".");
  let domain;
  if (host.length > 3) domain = "." + host.slice(-3).join(".");
  else if (host.length > 1) domain = "." + host.slice(-2).join(".");
  else domain = window.location.hostname;
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;domain=${domain}`;
}

function getCookie(name) {
  const arr = document.cookie.split(";");
  for (let i = 0; i < arr.length; i++) {
    const c = arr[i].trim();
    if (c.startsWith(name + "=")) return c.substring(name.length + 1);
  }
  return null;
}

export default function BookingPage() {
  useEffect(() => {
    document.title = "Search Flights · JetsetLess";

    // Widget config
    window.TPWL_CONFIGURATION = { version: "v2", ab_flag: "", ab_variant: "", ab_evaluation_id: "" };
    window.TPWL_EXTRA = {
      currency: TRAVELPAYOUTS.CURRENCY,
      marker: TRAVELPAYOUTS.MARKER,
      trs: TRAVELPAYOUTS.TRS,
      domain: TRAVELPAYOUTS.DOMAIN,
      locale: TRAVELPAYOUTS.LOCALE,
      link_color: TRAVELPAYOUTS.LINK_COLOR,
    };
    window.tpwlCurrencyChange = (v) => setDomainCookie("tpwl_currency", v);
    window.tpwlLocaleChange = (v) => { setDomainCookie("tpwl_locale", v); window.location.reload(); };

    // Main TP White Label script
    if (!document.querySelector('script[data-tpwl-main="1"]')) {
      const s = document.createElement("script");
      s.async = 1;
      s.type = "module";
      s.src = TP_SCRIPT_SRC;
      s.setAttribute("data-tpwl-main", "1");
      s.setAttribute("data-testid", "tp-embed-script");
      document.head.appendChild(s);
    }

    // Popular destination weedles
    const weedleRoot = document.getElementById("tpwl-widget-weedles");
    if (weedleRoot) {
      weedleRoot.querySelectorAll('div[is="weedle"]').forEach((el) => {
        if (el.dataset.loaded === "1") return;
        const dest = el.getAttribute("data-destination");
        const sc = document.createElement("script");
        sc.async = 1;
        sc.src = `https://tpwidg.com/content?currency=${TRAVELPAYOUTS.CURRENCY.toLowerCase()}&trs=${TRAVELPAYOUTS.TRS}&shmarker=${TRAVELPAYOUTS.MARKER}&destination=${dest}&target_host=${TRAVELPAYOUTS.DOMAIN}&locale=${TRAVELPAYOUTS.LOCALE}&limit=6&powered_by=false&primary=%23${TRAVELPAYOUTS.LINK_COLOR}&promo_id=4044&campaign_id=100`;
        el.appendChild(sc);
        el.dataset.loaded = "1";
      });
    }

    // Auto-hide popular destinations when ticket results are visible + scroll on flightSearch param
    const widgetsBlock = document.querySelector(".tpwl-widgets__wrapper");
    const resultsBlock = document.getElementById("tpwl-tickets");
    const searchHeader = document.querySelector(".tpwl-search-header");
    const syncVisibility = () => {
      if (!widgetsBlock || !resultsBlock) return;
      const hasResults = resultsBlock.offsetHeight > 0;
      const hasFlightSearch = /flightSearch=\w+/.test(window.location.href);
      widgetsBlock.style.display = (hasResults || hasFlightSearch) ? "none" : "flex";
    };
    const scrollToResults = () => {
      if (!resultsBlock) return;
      const headerHeight = searchHeader?.clientHeight || 0;
      window.scrollTo({ top: resultsBlock.offsetTop - headerHeight - 80, behavior: "smooth" });
    };
    if (/flightSearch=\w+/.test(window.location.href)) setTimeout(scrollToResults, 1500);
    const syncInterval = setInterval(syncVisibility, 400);
    setTimeout(() => clearInterval(syncInterval), 20000);

    // Cookie banner
    const banner = document.getElementById("tpwl-cookie-banner");
    const acceptBtn = document.getElementById("tpwl-accept-cookie-btn");
    if (banner && getCookie("tpwl_cookie_accepted") !== "true") {
      banner.style.display = "flex";
      if (acceptBtn) {
        acceptBtn.onclick = () => {
          setDomainCookie("tpwl_cookie_accepted", "true");
          banner.style.display = "none";
        };
      }
    }

    // Affiliate click tracking
    const trackClick = (ev) => {
      const a = ev.target.closest("a[href]");
      if (!a) return;
      const host = a.closest("#tpwl-tickets, #tpwl-search, .tpwl-widget-weedle");
      if (!host) return;
      const href = a.getAttribute("href") || "";
      if (!/^https?:\/\//i.test(href)) return;
      const destAttr = host.closest("[data-destination]")?.getAttribute("data-destination");
      const row = a.closest("[class*=ticket], [class*=result], [class*=weedle]") || host;
      const text = row?.textContent || "";
      const iata = text.match(/\b([A-Z]{3})\b[^A-Z]*\b([A-Z]{3})\b/);
      const price = text.match(/\$\s?([0-9][0-9,]*)/);
      trackEvent({
        event_type: "booking_click",
        origin: iata?.[1],
        destination: destAttr || iata?.[2],
        price_cash: price ? Number(price[1].replace(/,/g, "")) : undefined,
      });
    };
    document.addEventListener("click", trackClick, true);

    return () => {
      clearInterval(syncInterval);
      document.removeEventListener("click", trackClick, true);
    };
  }, []);

  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-[#F9F9F6]" data-testid="booking-page">
      <Header />

      {/* 1) Logo header — "Flight tickets" + H1 (TP template section #1) */}
      <header className="tpwl-logo-header" data-testid="tpwl-logo-header">
        <div className="tpwl-logo__wrapper">
          <div className="tpwl-logo__logo" aria-hidden />
          <span>Flight tickets</span>
        </div>
        <div className="tpwl-search__wrapper">
          <div className="tpwl__content">
            <h1 data-testid="booking-title">Your journey begins here</h1>
          </div>
        </div>
      </header>

      {/* 2) Sticky search form header (TP template section #2) */}
      <header className="tpwl-search-header" data-testid="tpwl-search-header">
        <div className="tpwl-search__wrapper">
          <div className="tpwl__content">
            <div id="tpwl-search" data-testid="tpwl-search" />
          </div>
        </div>
      </header>

      {/* 3) Main: tickets + popular destinations (TP template section #3) */}
      <main className="tpwl-main">
        <div className="tpwl-tickets__wrapper">
          <div className="tpwl__content">
            <div id="tpwl-tickets" data-testid="tpwl-tickets" />
          </div>
        </div>

        <div className="tpwl-widgets__wrapper">
          <div className="tpwl__content">
            <h3>Popular destinations</h3>
            <div id="tpwl-widget-weedles" className="tpwl-widget-weedles">
              {POPULAR_DESTINATIONS.map((iata) => (
                <div key={iata} className="tpwl-widget-weedle" data-destination={iata} is="weedle" data-testid={`weedle-${iata}`} />
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* 4) TP-style links footer (TP template section #4) */}
      <footer className="tpwl-footer__wrapper" data-testid="tpwl-footer">
        <div className="tpwl__content">
          <div className="tpwl-footer__copyright">JetsetLess Media © {year}</div>
          <div className="tpwl-footer__links">
            <a href="/about">Terms of Service</a>
            <a href="/about">Privacy Policy</a>
            <a href="/about">Cookie Policy</a>
          </div>
        </div>
      </footer>

      {/* 5) Main JetsetLess footer (brand footer) */}
      <Footer />

      {/* 6) Cookie banner (TP template section #5) */}
      <div className="tpwl-cookie-banner" id="tpwl-cookie-banner" style={{ display: "none" }}>
        <div className="tpwl-cookie-banner__logo" />
        <div className="tpwl-cookie-banner__text">To provide a better experience we use cookies to store your search history and selected filters. By clicking or navigating the site, you agree to allow our collection of information through cookies.</div>
        <div className="tpwl-cookie-banner__actions">
          <button className="tpwl-cookie-banner__base-button tpwl-cookie-banner__accept" id="tpwl-accept-cookie-btn" data-testid="accept-cookies-btn">Accept</button>
          <a className="tpwl-cookie-banner__base-button tpwl-cookie-banner__link" href="/about" target="_blank" rel="noreferrer">Cookies Policy</a>
        </div>
      </div>
    </div>
  );
}
